import { useState, useEffect, useCallback } from 'react';

interface DebugLog {
  timestamp: number;
  source: 'service-worker' | 'app';
  message: string;
}

const MAX_LOGS = 50;
const STORAGE_KEY = 'debug-logs';

// Fonction pour ajouter un log depuis n'importe où (y compris le service worker)
export const addDebugLog = (source: 'service-worker' | 'app', message: string) => {
  const log: DebugLog = {
    timestamp: Date.now(),
    source,
    message,
  };

  // Stocker dans localStorage pour que le hook puisse le récupérer
  try {
    const existingLogs = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]') as DebugLog[];
    existingLogs.push(log);
    // Garder seulement les N derniers logs
    const recentLogs = existingLogs.slice(-MAX_LOGS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(recentLogs));
    
    // Émettre un événement personnalisé pour notifier le hook
    window.dispatchEvent(new CustomEvent('debug-log-added', { detail: log }));
  } catch (e) {
    console.error('Failed to store debug log:', e);
  }
};

// Hook pour récupérer et afficher les logs
export const useDebugLogs = () => {
  const [logs, setLogs] = useState<DebugLog[]>([]);

  const loadLogs = useCallback(() => {
    try {
      const storedLogs = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]') as DebugLog[];
      setLogs(storedLogs);
    } catch (e) {
      console.error('Failed to load debug logs:', e);
      setLogs([]);
    }
  }, []);

  const clearLogs = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setLogs([]);
  }, []);

  useEffect(() => {
    // Charger les logs au montage
    loadLogs();

    // Écouter les nouveaux logs
    const handleNewLog = () => {
      loadLogs();
    };

    window.addEventListener('debug-log-added', handleNewLog);
    
    // Recharger périodiquement (au cas où le service worker ajoute des logs)
    const interval = setInterval(loadLogs, 1000);

    return () => {
      window.removeEventListener('debug-log-added', handleNewLog);
      clearInterval(interval);
    };
  }, [loadLogs]);

  return { logs, clearLogs, refreshLogs: loadLogs };
};

